import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaEnvelope, FaMobileAlt, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../firebase";
import { AuthContext } from "../context/AuthContext";
import api from "../utils/base_url";

const Login = () => {
  const [loginMethod, setLoginMethod] = useState("phone");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState("");



  const { login, user } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const redirectData =
        sessionStorage.getItem("login_redirect");

      if (redirectData) {
        const parsed = JSON.parse(
          redirectData
        );

        // clear after use
        sessionStorage.removeItem(
          "login_redirect"
        );

        navigate(parsed.pathname, {
          replace: true,
          state: parsed.state,
        });
      } else {
        // normal login
        navigate("/", {
          replace: true,
        });
      }
    }
  }, [user, navigate]);

  // Validation helpers

  const isValidPhone = (phone) => /^[0-9]{10,15}$/.test(phone);


  // Email login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const normalizedEmail = email.trim().toLowerCase(); // ✅ FIX

      const res = await api.post("customer/login/", { email: normalizedEmail, password });
      login(res.data);
      toast.success("Logged in successfully!");
    } catch (error) {
      setErrors(error.response?.data?.error || "Invalid email or password");
      setTimeout(() => {
        setErrors("");
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const msg = sessionStorage.getItem("redirect_toast");
    if (msg) {
      toast.success(msg, { duration: 3000 });
      sessionStorage.removeItem("redirect_toast"); // remove after showing
    }
  }, []);

  // Phone login
  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    if (!isValidPhone(phone)) return toast.error("Please enter a valid phone number.");

    setLoading(true);
    try {
      const res = await api.post("phone-login-step1/", { phone });
      sessionStorage.setItem("session_id", res.data.session_id);
      sessionStorage.setItem("phone", phone);
      toast.success(res.data.message || "OTP sent successfully!");
      navigate("/verification", { state: { type: "phone" } });
    } catch (error) {
      const msg =
        error.response?.data?.error || error.response?.data?.message || "Login failed";
      setErrors(msg);

      setTimeout(() => {
        setErrors("");
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  // Google login
  const handleGoogleLogin = async () => {
    setLoading(true);
    setErrors("");

    try {
      // 🔹 Step 1: Open Google popup
      const result = await signInWithPopup(auth, googleProvider);

      if (!result?.user) {
        throw new Error("Google authentication cancelled");
      }

      // 🔹 Step 2: Get Firebase ID token
      const idToken = await result.user.getIdToken(true); // force refresh

      // 🔹 Step 3: Send token to backend
      const res = await api.post("google/", { token: idToken });

      // 🔹 Step 4: Login success
      login(res.data);
      toast.success("Logged in with Google!");
    } catch (error) {
      console.error("Google Login Error:", error);

      let message = "Google login failed";
      let errorCode = error?.code || "UNKNOWN_GOOGLE_ERROR";

      // 🔹 Firebase popup & network errors
      if (error?.code) {
        switch (error.code) {
          case "auth/popup-closed-by-user":
            message = "Google login popup closed";
            break;

          case "auth/popup-blocked":
            message = "Popup blocked. Please allow popups.";
            break;

          case "auth/network-request-failed":
            message = "Network error. Please check your internet connection.";
            break;

          default:
            message = error.message || message;
        }
      }

      // 🔹 Backend error response (Google token / server issues)
      if (error?.response?.data?.error) {
        message = error.response.data.error;
        errorCode = "BACKEND_GOOGLE_ERROR";
      }

      // 🔹 Send frontend-only error to backend logs (NON-BLOCKING)
      try {
        await api.post("frontend-auth-log/", {
          action: "GOOGLE_LOGIN",
          login_type: "GOOGLE",
          status: "FAILURE",
          message: message,
          error_code: errorCode,
          identifier: error?.customData?.email || null,
        });
      } catch (logError) {
        // ❌ Never break UI if logging fails
        console.warn("Frontend auth log failed", logError);
      }

      // 🔹 UI feedback
      setErrors(message);
      toast.error(message);

      setTimeout(() => setErrors(""), 3000);

    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper">
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="login-card"
      >
        <h5 className="login-title">Welcome Back!</h5>

        <img
          src={`${process.env.PUBLIC_URL}/Logo.png`}
          alt="Logo"
          className="navbar-logo"
        />


        {/* Login method toggle */}
        <div className="login-method-toggle">
          <button
            className={loginMethod === "phone" ? "active" : ""}
            onClick={() => setLoginMethod("phone")}
          >
            <FaMobileAlt size={20} />
          </button>
          <button
            className={loginMethod === "email" ? "active" : ""}
            onClick={() => setLoginMethod("email")}
          >
            <FaEnvelope size={20} />
          </button>
        </div>

        {/* Email Login */}
        {loginMethod === "email" && (
          <motion.form onSubmit={handleEmailLogin} className="login-form">
            <div className="floating-input">
              <input
                type="email"
                placeholder=""
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
              />
              <label>
                <FaEnvelope /> Email
              </label>
            </div>

            <div className="floating-input password-input">
              <input
                type={showPassword ? "text" : "password"}
                placeholder=""
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
              />
              <label>
                <FaLock /> Password
              </label>
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {typeof errors === "string" && errors.trim() !== "" && (
              <p style={{ color: "red", textAlign: "center", marginTop: "-10px" }}>
                {errors}
              </p>
            )}


            <div className="forgot-password">
              <Link to="/passwordreset">Forgot Password?</Link>
            </div>

            <button className="btn btn-primary login-btn" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </button>
          </motion.form>
        )}

        {/* Phone Login */}
        {loginMethod === "phone" && (
          <motion.form onSubmit={handlePhoneLogin} className="login-form">
            <div className="floating-input">
              <input
                type="tel"
                placeholder=" "
                value={phone}
                required
                onChange={(e) => setPhone(e.target.value.replace(/\D/, ""))}
                minLength={10}
                maxLength={10}
              />
              <label>
                <FaMobileAlt /> Phone Number
              </label>
            </div>
            {typeof errors === "string" && errors.trim() !== "" && (
              <p style={{ color: "red", textAlign: "center", marginTop: "-10px" }}>
                {errors}
              </p>
            )}

            <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
              {loading ? "Sending OTP..." : "Send OTP"}
            </button>
          </motion.form>
        )}

        {/* Google Login */}
        <button onClick={handleGoogleLogin} className="btn btn-google-login" disabled={loading}
          style={{ border: "1px solid #8A4B27" }}
        >
          <FcGoogle size={24} />
          {loading ? "Please wait..." : "Login with Google"}
        </button>

        <p className="register-prompt">
          New here?{" "}
          <Link to="/register" className="register-link">
            Register
          </Link>
        </p>
      </motion.div>

      {/* 🌿 Green Theme Styles */}
      <style>{`

      .navbar-logo {
  height: 50px;       /* reduce from 100px (too big for navbar) */
  width: auto;       /* keep clarity */
  display: block;
  margin-bottom: 12px;
  
}
  @media (max-width: 576px) {
  .navbar-logo {
    max-width: 300px;   /* smaller for mobile */
    margin: 8px auto 15px auto;
  }
}

        .login-wrapper {
          min-height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          background: #E9DCC3;
          padding: 20px;
        }
        .login-card {
          width: 100%;
          max-width: 420px;
          background: #E9DCC3;
          border-radius: 20px;
          border: 1px solid #8A4B27;
          padding: 40px 30px;
          box-shadow: 0 10px 25px #4A1F1A;
          text-align: center;
        }
        .login-title {
          font-size: 1.8rem;
          margin-bottom: 12px;
          font-weight: 700;
          color: #000000ff;
        }
      
        .forgot-password {
          text-align: right;
          margin-top: -15px;
        }
        .forgot-password a {
          color: #4A1F1A;
          font-weight: 600;
          text-decoration: underline;
          font-size: 0.95rem;
        }
        .login-method-toggle {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 30px;
  padding: 10px;
  background: #ffffffff;
  border-radius: 14px;
   border: 1.5px solid #4A1F1A;
}

.login-method-toggle button {
  width: 64px;
  height: 50px;
  border-radius: 12px; /* 🔲 box */
  border: 2px solid #4A1F1A;
  background: #E9DCC3;
  color:  #4A1F1A;;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 22px;
  transition: all 0.3s ease;
}

.login-method-toggle button:hover {
  background: #E9DCC3;
}

.login-method-toggle button.active {
  background: #4A1F1A;
  color: #E9DCC3;
  box-shadow: 0 6px 15px #4A1F1A;
}

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 25px;
          
        }
        .floating-input {
          position: relative;
        }
        .floating-input input {
          width: 100%;
          padding: 14px 14px 14px 42px;
          font-size: 1.1rem;
          border-radius: 15px;
          border: 1.5px solid #cde6ce;
          background: #f9fff9;
          color: #000000ff;
          font-weight: 600;
          outline: none;
        }
        .floating-input label {
          position: absolute;
          left: 42px;
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
          color: #000000ff;
          font-weight: 600;
          transition: 0.3s;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .floating-input input:focus + label,
        .floating-input input:not(:placeholder-shown) + label {
          top: 5px;
          font-size: 0.85rem;
          color: #8A4B27;
          background: #f9fff9;
          padding: 0 8px;
          border-radius: 8px;
          left: 15px;
        }
        .password-input { position: relative; }
        .password-toggle {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          border: none;
          background: transparent;
          color: #000000ff;
          cursor: pointer;
          font-size: 1.25rem;
        }

        /* ✅ Hover effect for Login Button */
        .btn-primary.login-btn {
          background: #4A1F1A;
          color: #fff;
          border-radius: 8px;
          padding: 14px 0;
          font-weight: 700;
          font-size: 1.2rem;
          border: none;
          cursor: pointer;
          transition: background-color 0.3s ease;
          box-shadow: 0 6px 18px #8A4B27 ;
        }
        .btn-primary.login-btn:hover:not(:disabled) {
          background: #8A4B27;
          box-shadow: 0 6px 18px #4A1F1A ;
        }
        .btn-primary.login-btn:disabled {
          background: #8A4B27;
          
          opacity: 0.7;
          cursor: not-allowed;
        }

        .btn-google-login {
margin-top: 35px;
          background: #000000ff important;
          border: none;
          border-radius: 14px; /* 🔲 BOX */
          padding: 14px 0;
          font-weight: 700;
          font-size: 1.15rem;
          color: #000000;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 14px;
          width: 100%;
          box-shadow: 0 6px 18px #8A4B27;
          transition: all 0.3s ease;
}

.btn-google-login:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px #4A1F1A;
}

.btn-google-login:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

        .register-prompt {
          margin-top: 40px;
          color: #000000ff;
          font-weight: 600;
        }
        .register-link {
          color: #4A1F1A;
          font-weight: 700;
          text-decoration: underline;
        }
          .register-link:hover {
          color: #8A4B27;
        }
      `}</style>
    </div>
  );
};

export default Login;
